import { Button } from '@/ui/Button'
import { cn } from '@/ui/lib/utils'
import type { QuantChartModel } from '@/ui/lib/quantChart/types'
import { Select } from '@/ui/Select'
import { QuantChart, type ChartViewCommand, type QuantViewChange } from '@/ui/QuantChart'

export type QuantPreviewStatus =
  'loading' | 'updating' | 'ready' | 'invalid' | 'unavailable' | 'failed' | 'not_covered'
export type QuantPreviewSpan = 120 | 240 | 600 | 'all'

/** The message block over the chart; the caller chooses the text, the panel only lays it out. */
export type QuantPreviewBody =
  | { kind: 'loading'; slow: boolean }
  | { kind: 'invalid'; text: string }
  | { kind: 'not_covered'; message: string; missingLine: string | null }
  | { kind: 'failed'; detail: string }
  | { kind: 'unavailable'; headline: string }

/** The controls row (spec 039 3.1): instrument, timeframe, bars and the mismatch line. Props in, callbacks out. */
export interface QuantPreviewControls {
  instruments: string[]
  /** Spec 073: the display label of an instrument option (`NQ (Databento)`); the value stays the bare name. Absent shows the name. */
  instrumentLabel?: (instrument: string) => string
  onInstrument: (instrument: string) => void
  timeframes: string[]
  onTimeframe: (tf: string) => void
  /** Bar-count choices for the current timeframe; the last one is the cap. */
  barOptions: number[]
  bars: number
  onBars: (bars: number) => void
  /** The one-line "preview shows X; the form says Y" text, or `null`. */
  mismatch: string | null
  /** The quiet "data through <date>" text, or `null` when there is nothing to say. */
  dataThrough: string | null
  /** Spec 040: the "no live feed" line. Replaces the `data through` label (it carries the date); yields to the mismatch line. */
  feedHint?: string | null
  /** The "Use preview range" button: its enabled state, hover/disabled-reason title and handler. `null` hides it. */
  useRange: { enabled: boolean; title: string; onUse: () => void } | null
  /** Spec 070: why this is the still preview ("no live preview", or the cap sentence), or `null`. */
  liveLabel?: string | null
  /** Spec 070: the 1 s / 5 s tick rate of the live view; `null`/absent while the still preview is shown. */
  rate?: { value: 1 | 5; onChange: (rate: 1 | 5) => void } | null
}

const SPANS: Array<{ value: QuantPreviewSpan; label: string }> = [
  { value: 120, label: '120' },
  { value: 240, label: '240' },
  { value: 600, label: '600' },
  { value: 'all', label: 'All' },
]

const STATUS: Record<
  QuantPreviewStatus,
  { label: string; text: string; dot: string; pulse: boolean }
> = {
  loading: { label: 'Loading', text: 'text-paper-50', dot: 'bg-paper-50', pulse: false },
  updating: { label: 'Updating', text: 'text-[#CB8A00]', dot: 'bg-[#CB8A00]', pulse: false },
  ready: { label: 'Ready', text: 'text-accent-line', dot: 'bg-accent-line', pulse: false },
  invalid: { label: 'Params invalid', text: 'text-alert', dot: 'bg-alert', pulse: false },
  unavailable: { label: 'Unavailable', text: 'text-alert', dot: 'bg-alert', pulse: false },
  failed: { label: 'Preview failed', text: 'text-alert', dot: 'bg-alert', pulse: false },
  not_covered: { label: 'No data', text: 'text-[#CB8A00]', dot: 'bg-[#CB8A00]', pulse: false },
}

const QUIET_NOTE = "Showing the latest bars; the form's date range applies to the backtest only."

export interface QuantPreviewPanelProps {
  instrument: string
  /** Timeframe label source (`5m`) and the candles held. */
  tf: string
  count: number
  status: QuantPreviewStatus
  model: QuantChartModel
  /** Which window button is pressed (`null` after a manual zoom). */
  pressed: QuantPreviewSpan | null
  /** A window button was pressed. */
  onWindow: (span: QuantPreviewSpan) => void
  /** The chart's view command (applied when `seq` changes) and its read-back callback. */
  view: { seq: number; command: ChartViewCommand }
  onViewChange: (change: QuantViewChange) => void
  overlaySeq: number
  candleFrames: number
  /** Overlay stats to print in the footer, or `null` while none apply (see `statsNote`). */
  stats: Array<[string, string | number]> | null
  /** Footer text shown instead of stats (e.g. the backend is computing, or there is no overlay). */
  statsNote: string
  /** What the message block over the chart says (state dependent), or `null` for none. */
  body: QuantPreviewBody | null
  /** The Retry button of the `failed` and `unavailable` bodies. */
  onRetry: () => void
  /** A request has been running longer than 600 ms: show the progress strip. */
  showStrip: boolean
  /** Draw the overlays dimmed (a slow update, or a failure kept over held candles). */
  dim: boolean
  /** The strategy's legend entries (may be empty). */
  legend: string[]
  /** The instrument, timeframe and bars controls; absent renders an empty reserved row. */
  controls?: QuantPreviewControls
}

/**
 * The Config tab's live preview chart (handoff 3.3, spec 039 3.1): a fixed
 * 36px header with the preview state and window segment, a reserved 40px
 * controls row, a 2px strip, a fixed 380px canvas, a 36px stats row and a
 * fixed 40px legend row. No state changes its height.
 * Props in, callbacks out; the session itself lives in `useQuantPreview`.
 */
export function QuantPreviewPanel({
  instrument,
  tf,
  count,
  status,
  model,
  pressed,
  onWindow,
  view,
  onViewChange,
  overlaySeq,
  candleFrames,
  stats,
  statsNote,
  body,
  onRetry,
  showStrip,
  dim,
  legend,
  controls,
}: QuantPreviewPanelProps) {
  const s = STATUS[status]
  const legendText = legend.join(' · ')
  return (
    <div data-quant-preview data-status={status} className="min-w-0 border border-paper-14">
      <div className="flex h-9 items-center gap-x-3.5 overflow-hidden border-b border-paper-14 px-3.5">
        <span className="shrink-0 font-sans text-[11px] font-bold tracking-[0.2em] uppercase">
          Live preview
        </span>
        <span className="shrink-0 font-sans text-[12px] font-bold tracking-[0.14em] text-accent-line">
          {instrument}
        </span>
        <span className="shrink-0 font-mono text-[10px] tracking-[0.14em] whitespace-nowrap text-paper-40 uppercase">
          {tf.toUpperCase()} · {count.toLocaleString('en-US')} candles
        </span>
        <span className="min-w-0 flex-1" />
        <span
          role="status"
          className={cn(
            'flex shrink-0 items-center gap-1.5 font-mono text-[10px] tracking-[0.14em] whitespace-nowrap uppercase',
            s.text,
          )}
        >
          <i
            aria-hidden="true"
            className={cn(
              'block h-1.5 w-1.5',
              s.dot,
              s.pulse && 'animate-[q-pulse_1.2s_steps(1)_infinite] motion-reduce:animate-none',
            )}
          />
          {s.label}
        </span>
        <div
          role="group"
          aria-label="Preview window"
          className="flex shrink-0 border border-paper-14"
        >
          {SPANS.map((opt) => (
            <button
              key={opt.label}
              type="button"
              aria-pressed={pressed === opt.value}
              onClick={() => onWindow(opt.value)}
              className={cn(
                'cursor-pointer border-0 px-[9px] py-1 font-mono text-[10.5px] tracking-[0.08em]',
                pressed === opt.value ? 'bg-accent-line text-ink' : 'bg-transparent text-paper-50',
              )}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>
      <div
        data-controls-row
        className="flex h-10 flex-wrap items-center gap-x-3.5 gap-y-1 overflow-hidden border-b border-paper-14 px-3.5 max-[899px]:h-auto max-[899px]:min-h-10 max-[899px]:py-1"
      >
        {controls !== undefined && (
          <PreviewControls controls={controls} instrument={instrument} tf={tf} />
        )}
      </div>
      <div className="relative h-0.5 overflow-hidden bg-paper-14">
        {showStrip && (
          <div
            role="progressbar"
            aria-label="Preview request in progress"
            className="absolute inset-y-0 left-0 w-[28%] animate-[q-sweep_1.4s_linear_infinite] bg-accent-line motion-reduce:animate-none"
          />
        )}
      </div>
      <div className="relative h-[380px]">
        <QuantChart
          model={model}
          view={view}
          initialWidth={240}
          onViewChange={onViewChange}
          overlayRev={overlaySeq}
          overlayDim={dim}
          declutterLabels
          candleChunks={candleFrames}
        />
        {body !== null && <PreviewBodyBlock body={body} onRetry={onRetry} />}
      </div>
      <div className="flex h-9 items-center gap-x-6 overflow-hidden border-t border-paper-14 px-3.5">
        {stats !== null && stats.length > 0 ? (
          stats.map(([k, v]) => (
            <div key={k} className="flex shrink-0 items-baseline gap-1.5">
              <span className="font-mono text-[9px] tracking-[0.16em] text-paper-40 uppercase">
                {k}
              </span>
              <span className="font-mono text-[12px] text-paper-90">{String(v)}</span>
            </div>
          ))
        ) : (
          <span className="shrink-0 font-mono text-[11px] text-paper-40">{statsNote}</span>
        )}
        <span
          className="min-w-0 flex-1 truncate text-right font-mono text-[10px] text-paper-40"
          title={QUIET_NOTE}
        >
          {QUIET_NOTE}
        </span>
      </div>
      <div
        data-legend-row
        title={legendText}
        className="h-10 overflow-hidden border-t border-paper-14 px-3.5 py-1 font-mono text-[10px] leading-[14px] text-paper-50"
      >
        <div className="line-clamp-2 [overflow-wrap:anywhere]">{legendText}</div>
      </div>
    </div>
  )
}

function PreviewBodyBlock({ body, onRetry }: { body: QuantPreviewBody; onRetry: () => void }) {
  const label = 'font-mono text-[10px] tracking-[0.2em] uppercase'
  const retry = (
    <button
      type="button"
      data-action="retry-preview"
      onClick={onRetry}
      className="cursor-pointer border border-paper-50 bg-transparent px-3 py-1 font-mono text-[10.5px] tracking-[0.14em] text-paper uppercase"
    >
      Retry
    </button>
  )
  let content
  switch (body.kind) {
    case 'loading':
      content = (
        <>
          <div className={cn(label, 'text-paper-40')}>LOADING PREVIEW…</div>
          {body.slow && (
            <div className="font-mono text-[10px] text-paper-40">
              first request can take several seconds
            </div>
          )}
        </>
      )
      break
    case 'invalid':
      content = <div className={cn(label, 'text-paper-40')}>{body.text}</div>
      break
    case 'not_covered':
      content = (
        <>
          <div className={cn(label, 'text-[#CB8A00]')}>NO STORED DATA FOR THIS WINDOW</div>
          <div className="line-clamp-3 font-mono text-[11px] text-paper-90" title={body.message}>
            {body.message}
          </div>
          {body.missingLine !== null && (
            <div
              className="line-clamp-2 font-mono text-[10.5px] text-paper-50"
              title={body.missingLine}
            >
              {body.missingLine}
            </div>
          )}
        </>
      )
      break
    case 'failed':
      content = (
        <>
          <div className={cn(label, 'text-alert')}>THE STRATEGY RAISED AN ERROR</div>
          <div
            className="line-clamp-4 font-mono text-[11px] text-paper-90 [overflow-wrap:anywhere]"
            title={body.detail}
          >
            {body.detail}
          </div>
          {retry}
        </>
      )
      break
    case 'unavailable':
      content = (
        <>
          <div className="line-clamp-3 font-mono text-[11px] text-paper-90" title={body.headline}>
            {body.headline}
          </div>
          {retry}
        </>
      )
      break
  }
  const interactive = body.kind === 'failed' || body.kind === 'unavailable'
  return (
    <div
      className={cn(
        'absolute inset-0 z-20 flex items-center justify-center',
        !interactive && 'pointer-events-none',
      )}
    >
      <div className="flex max-w-[560px] flex-col items-center gap-2 bg-ink/80 px-5 py-3 text-center">
        {content}
      </div>
    </div>
  )
}

function PreviewControls({
  controls,
  instrument,
  tf,
}: {
  controls: QuantPreviewControls
  instrument: string
  tf: string
}) {
  const last = controls.barOptions[controls.barOptions.length - 1]
  const fmt = (n: number) => n.toLocaleString('en-US')
  return (
    <>
      <div className="w-44 shrink-0">
        <Select
          ariaLabel="Preview instrument"
          value={instrument}
          onValueChange={controls.onInstrument}
          options={controls.instruments.map((i) => ({ value: i, label: controls.instrumentLabel?.(i) ?? i }))}
          className="px-2 py-1 text-[11px] text-paper"
        />
      </div>
      <div
        role="group"
        aria-label="Preview timeframe"
        className="flex shrink-0 border border-paper-14"
      >
        {controls.timeframes.map((t) => (
          <button
            key={t}
            type="button"
            aria-pressed={t === tf}
            onClick={() => controls.onTimeframe(t)}
            className={cn(
              'w-[34px] cursor-pointer border-0 px-0 py-1 text-center font-mono text-[10.5px] tracking-[0.04em]',
              t === tf ? 'bg-accent-line text-ink' : 'bg-transparent text-paper-50',
            )}
          >
            {t}
          </button>
        ))}
      </div>
      <div className="flex shrink-0 items-center gap-1.5">
        <span className="font-mono text-[9px] tracking-[0.16em] text-paper-40 uppercase">Bars</span>
        <div className="w-28">
          <Select
            ariaLabel="Preview bars"
            value={String(controls.bars)}
            onValueChange={(v) => controls.onBars(Number(v))}
            options={controls.barOptions.map((n) => ({
              value: String(n),
              label: n === last ? `${fmt(n)} max` : fmt(n),
            }))}
            className="px-2 py-1 text-[11px] text-paper"
          />
        </div>
      </div>
      {controls.rate != null && (
        <div role="group" aria-label="Update rate" className="flex shrink-0 border border-paper-14">
          {([1, 5] as const).map((r) => (
            <button
              key={r}
              type="button"
              aria-pressed={controls.rate?.value === r}
              onClick={() => controls.rate?.onChange(r)}
              className={cn(
                'cursor-pointer border-0 px-2 py-1 font-mono text-[10.5px] tracking-[0.04em]',
                controls.rate?.value === r ? 'bg-accent-line text-ink' : 'bg-transparent text-paper-50',
              )}
            >
              {r} s
            </button>
          ))}
        </div>
      )}
      {controls.liveLabel != null && (
        <span data-live-label title={controls.liveLabel} className="shrink-0 truncate font-mono text-[10px] text-[#CB8A00]">
          {controls.liveLabel}
        </span>
      )}
      {controls.mismatch === null && controls.feedHint != null && (
        <span
          data-feed-hint
          title={controls.feedHint}
          className="min-w-0 flex-1 truncate font-mono text-[10px] text-paper-40"
        >
          {controls.feedHint}
        </span>
      )}
      {controls.mismatch === null && controls.feedHint == null && controls.dataThrough !== null && (
        <span
          data-data-through
          title={controls.dataThrough}
          className="w-[190px] shrink-0 truncate font-mono text-[10px] text-paper-40"
        >
          {controls.dataThrough}
        </span>
      )}
      {controls.mismatch !== null && (
        <span
          role="status"
          data-preview-mismatch
          title={controls.mismatch}
          className="min-w-0 flex-1 truncate font-mono text-[10px] text-[#CB8A00]"
        >
          {controls.mismatch}
        </span>
      )}
      {controls.useRange !== null && (
        <Button
          variant="secondary"
          size="sm"
          data-action="use-preview-range"
          disabled={!controls.useRange.enabled}
          title={controls.useRange.title}
          onClick={controls.useRange.onUse}
          className="ml-auto text-paper disabled:border-paper-14 disabled:text-paper-40"
        >
          Use preview range
        </Button>
      )}
    </>
  )
}
